import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { createSelector } from 'reselect'
import {
  get,
  isEmpty,
  debounce,
  intersection,
  orderBy,
  filter,
  isNaN,
  isNumber,
  cloneDeep,
  groupBy,
  find,
} from 'lodash'
import {
  ADD_FAVORITE,
  REMOVE_FAVORITE,
  GET_FAVORITES,
  GET_COLLECTIONS,
  GET_CONTRIBUTION_SETTINGS,
  GET_COMPANY,
  GET_CORPORATE_ONBOARDING_AGREEMENTS,
  SET_CORPORATE_ONBOARDING_AGREEMENTS,
  GET_ONBOARDING_AGREEMENTS,
  SET_ONBOARDING_AGREEMENTS,
  GET_BANK_AUTHORIZATION,
  GET_INTERNAL_ONBOARDING_AGREEMENTS,
  GET_GENERAL_SETTING,
} from 'api/constants'
import breakpoints from 'theme/breakpoints'
import { isSafari } from './helpers'
import { 
  ROLES, 
  WALLET_VENDOR, 
  IS_PRODUCTION, 
  BASE_URL, 
  HOST,
} from 'utils/constants'
import moment from 'moment/moment'
import TagManager from 'react-gtm-module'

export const useGTM = config => {
  const location = useLocation()
  const user = useSelector(({ global }) => global.user)

  const fireGTM = useCallback(data => {
    if (IS_PRODUCTION) {
      TagManager.dataLayer({
        dataLayer: {
          event: config?.event,
          userID: user.id,
          appPage: location.pathname,
          ...data
        },
      })
    }
  }, [user.id])

  return useMemo(() => ({
    fireGTM, 
  }), [fireGTM])
}

export const useQuery = () => {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}

export const useRoles = () => {
  const user = useSelector(({ global }) => global.user)
  const { is_manager, is_admin } = user || {}

  return useMemo(() => {
    const allRoles = {
      isAdmin: is_admin,
      isManager: !!is_manager,
    }

    let userRoles = []
    let userRoleNames = []

    if (allRoles.isAdmin) {
      userRoles.push(ROLES.ADMIN)
      userRoleNames.push('Admin')
    }
    if (allRoles.isManager) {
      userRoles.push(ROLES.MANAGER)
      userRoleNames.push('Manager')
    }

    return {
      ...allRoles,
      userRoles,
      userRoleNames,
      hasAnyRole: !!userRoles.length,
      hasRole: (data = []) => !!intersection(data, userRoles).length,
      landingPage: is_admin ? '/admin/company' : '/',
      hasLoaded: !isEmpty(user),
    }
  }, [
    is_manager,
    is_admin,
  ])
}

export const useApi = (
  type, 
  select, 
  options = {},
) => {
  const dispatch = useDispatch()
  const {
    dependencies = [],
    fb = {},
    data = {},
  } = options
  const returnData = useSelector(({ global }) => global[select], shallowEqual)

  useEffect(() => {
    dispatch({ type, ...data })
  }, dependencies)

  return useMemo(() => returnData || fb, [returnData])
}

export const useGet = (type, select, fb = {}, ...data) => {
  const dispatch = useDispatch()
  const returnData = useSelector(({ global }) => global[select], shallowEqual)

  useEffect(() => {
    if (isEmpty(returnData)) dispatch({ type, ...data })
  }, [])

  return useMemo(() => returnData || fb, [returnData])
}

export const useFlags = () => {
  const dispatch = useDispatch()
  const user = useSelector(({ global }) => global.user) || {}
  const accessToken = useSelector(({ global }) => global.token)
  const insuranceDisabled = useSelector(({ global }) => global.insurance_disabled_setting)
  const contributionSettings = useSelector(({ global }) => global.contributionSettings) || null
  const {
    bank_actual_total,
    donde_credits_total,
    company_credits_total,
  } = useSelector(({ global }) => global.savingsTotals) || {}
  const { user_settings } = contributionSettings || {}
  const { 
    amount, 
    has_payroll,
    payroll_percentage_amount,
    payroll_fixed_amount,
  } = user_settings || {}
  const needsContribution = !+amount && !+payroll_percentage_amount && !+payroll_fixed_amount
  const company = useSelector(({ global }) => global.company) || null
  // const companyPageHidden = company?.enabled_features?.company_page_hidden
  const isClassicBooking = company?.enabled_features?.donde_classic_booking
  const corpPayrollEnabled = !!company?.payroll_enabled
  const hasMatchGroups = !!company?.match_settings?.length
  const hasWallet = user.has_savings_account
  const isWalletCapable = user.is_wallet_capable
  const hasAccount = !!user.primary_connected_account_id || has_payroll
  const nextStep = isWalletCapable && !hasWallet ? 'wallet' : hasWallet && !hasAccount ? 'account' : hasWallet && hasAccount && needsContribution ? 'savings' : null
  const isBond = user.vendor === WALLET_VENDOR.BOND
  const isPoints = [1, 2].includes(company.currency_config)
  const isHybridPoints = company.currency_config === 2
  const isPointsOnly = company.currency_config === 1
  const currentSavings = +bank_actual_total || 0
  const currentCompanyPoints = +company_credits_total || 0
  const currentPoints = +donde_credits_total || 0
  const hasPoints = !!+donde_credits_total
  const hasCompanyPoints = !!+company_credits_total
  const spendingPower = currentSavings + currentCompanyPoints + currentPoints

  useEffect(() => {
    if (accessToken) {
      if (company === null) dispatch({ type: GET_COMPANY })
      if (contributionSettings === null) dispatch({ type: GET_CONTRIBUTION_SETTINGS })
      if (insuranceDisabled === null) dispatch({ type: GET_GENERAL_SETTING, setting: 'insurance_disabled' })
    }
  }, [accessToken])

  return useMemo(() => {
    if (!accessToken || company === null || contributionSettings === null) {
      return {
        hiddenPages: [],
      }
    }
    return {
      hasWallet,
      isWalletCapable,
      hasAccount,
      // companyPageHidden,
      isClassicBooking,
      nextStep,
      // hiddenPages: [(company === null || companyPageHidden) && 'CompanyPage'].filter(o => !!o),
      hiddenPages: [],
      corpPayrollEnabled,
      hasMatchGroups,
      needsContribution,
      isBond,
      insuranceDisabled,
      isPoints,
      isHybridPoints,
      isPointsOnly,
      spendingPower,
      hasSpendingPower: !!spendingPower,
      flagsLoaded: !isEmpty(company) && !isEmpty(contributionSettings),
    }
  }, [
    hasWallet,
    isWalletCapable,
    hasAccount,
    // companyPageHidden,
    isClassicBooking,
    nextStep,
    company === null,
    contributionSettings === null,
    accessToken,
    corpPayrollEnabled,
    hasMatchGroups,
    needsContribution,
    isBond,
    insuranceDisabled,
    isPoints,
    isHybridPoints,
    isPointsOnly,
    spendingPower,
  ])
}

export const useCollectFavorite = (id, vendor) => {
  const dispatch = useDispatch()
  const favorites = useGet(GET_FAVORITES, 'favorites', null)
  const collections = useGet(GET_COLLECTIONS, 'collections', null)

  const [
    isFavorited,
    vendorUID,
  ] = useMemo(() => {
    let vendorContent = vendor ? find(favorites?.contents, { remote_product_id: id + '' }) : null
    return [
      vendor ? !!vendorContent : favorites?.content_ids?.includes(id),
      vendorContent?.unique_id,
    ]
  }, [favorites, id, vendor])

  const [
    connectedCollections,
    hasCollections,
    recommendedCollection,
    isCollected,
  ] = useMemo(() => {
    let temp = []
    collections?.forEach(collection => {
      if (
        (vendor && find(collection?.contents, { remote_product_id: id + '' })) || 
        collection.content_ids.includes(vendorUID || id)
      ) {
        temp.push(collection)
      }
    })
    return [
      temp,
      !!collections?.length,
      collections?.[0] || {},
      !isEmpty(temp),
    ]
  }, [collections, vendorUID, id])

  const toggleFavorite = () => {
    dispatch({
      type: isFavorited ? REMOVE_FAVORITE : ADD_FAVORITE,
      content_id: vendorUID || id,
      vendor,
    })
  }

  return useMemo(() => {
    if (favorites === null || collections === null) {
      return {
        collections: [],
        recommendedCollection: {},
        isLoading: true,
      }
    }
    return {
      isFavorited,
      isCollected,
      connectedCollections,
      hasCollections,
      collections: collections || [],
      recommendedCollection,
      isLoading: false,
      toggleFavorite,
    }
  }, [
    isFavorited,
    connectedCollections,
    collections,
    favorites,
    hasCollections,
    recommendedCollection,
    isCollected,
  ])
}

const selectIsTypeLoading = createSelector(
  state => state.typeLoading,
  (_, type) => type,
  (typeLoading, type) => typeLoading?.includes(type)
)

const selectErrorCode = createSelector(
  state => state,
  (_, type) => type,
  (state, type) => state[`error${type}`] || undefined
)

const selectErrorData = createSelector(
  state => state,
  (_, type) => type,
  (state, type) => state[`error${type}`]?.errorData || undefined
)

const selectSuccessData = createSelector(
  state => state,
  (_, type) => type,
  (state, type) => state[`success${type}`] || undefined
)

const selectStatusCode = createSelector(
  state => state,
  (_, type) => type,
  (state, type) => state[`statusCode${type}`] || undefined
)

export const useTypeLoad = (type, cb = () => {}) => {
  const isLoading = useSelector(({ global }) => selectIsTypeLoading(global, type))
  const error = useSelector(({ global }) => selectErrorCode(global, type))
  const errorData = useSelector(({ global }) => selectErrorData(global, type))
  const successData = useSelector(({ global }) => selectSuccessData(global, type))
  const statusCode = useSelector(({ global }) => selectStatusCode(global, type))
  const [isLoaded, setIsLoaded] = useState(false)
  const [startedLoading, setStartedLoading] = useState(false)

  const data = useMemo(() => ({
    isLoading,
    isLoaded,
    error,
    errorData,
    successData,
    statusCode,
  }), [isLoading, isLoaded, error, errorData, successData, statusCode])

  useEffect(() => {
    if (isLoaded) setIsLoaded(false)
  }, [type])

  useEffect(() => {
    if (isLoading) {
      setStartedLoading(true)
      setIsLoaded(false)
    } else if (startedLoading) {
      setStartedLoading(false)
      setIsLoaded(true)
      cb(data)
    }
  }, [isLoading])

  return data
}

const selectIsTypeArrLoading = createSelector(
  state => state.typeLoading,
  (_, types) => types,
  (typeLoading, types) => !!intersection(typeLoading, types)?.length
)

export const useTypeArrLoad = (types = []) => {
  const isLoading = useSelector(({ global }) =>
    selectIsTypeArrLoading(global, types)
  )

  return useMemo(() => ({
    isLoading,
  }), [isLoading])
}

export const useEventListener = (eventName, handler, element = window) => {
  const savedHandler = useRef()

  useEffect(() => {
    savedHandler.current = handler
  }, [handler])

  useEffect(() => {
    const isSupported = element && element.addEventListener
    if (!isSupported) return
    const eventListener = event => savedHandler.current(event)
    element.addEventListener(eventName, eventListener)
    return () => {
      element.removeEventListener(eventName, eventListener)
    }
  }, [eventName, element])
}

export const useScript = src => {
  // Keep track of script status ("idle", "loading", "ready", "error")
  const [status, setStatus] = useState(src ? 'loading' : 'idle')

  useEffect(() => {
    if (!src) {
      setStatus('idle')
      return
    }

    let script = document.querySelector(`script[src="${src}"]`)

    if (!script) {
      script = document.createElement('script')
      script.src = src
      script.async = true
      script.setAttribute('data-status', 'loading')
      document.body.appendChild(script)

      const setAttributeFromEvent = event => {
        script.setAttribute(
          'data-status',
          event.type === 'load' ? 'ready' : 'error'
        )
      }

      script.addEventListener('load', setAttributeFromEvent)
      script.addEventListener('error', setAttributeFromEvent)
    } else {
      setStatus(script.getAttribute('data-status'))
    }

    const setStateFromEvent = event => {
      setStatus(event.type === 'load' ? 'ready' : 'error')
    }

    script.addEventListener('load', setStateFromEvent)
    script.addEventListener('error', setStateFromEvent)

    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent)
        script.removeEventListener('error', setStateFromEvent)
      }
    }
  }, [src])
  return status
}

export const useOnClickOutside = (ref, handler, onUp) => {
  useEffect(() => {
    const listener = event => {
      if (
        ref?.current?.contains?.(event.target) ||
        ref?.contains?.(event.target)
      )
        return
      // if (!ref.current || ref.current.contains(event.target)) return
      handler(event)
    }

    document.addEventListener(onUp ? 'mouseup' : 'mousedown', listener)
    document.addEventListener(onUp ? 'touchend' : 'touchstart', listener)

    return () => {
      document.removeEventListener(onUp ? 'mouseup' : 'mousedown', listener)
      document.removeEventListener(onUp ? 'touchend' : 'touchstart', listener)
    }
  }, [ref, handler, onUp])
}

export const useInterval = (callback, delay) => {
  const savedCallback = useRef()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    const handler = (...args) => savedCallback.current(...args)

    if (delay !== null) {
      const id = setInterval(handler, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

export const useTimeout = (callback, delay) => {
  const savedCallback = useRef()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      let id = setTimeout(tick, delay)
      return () => clearTimeout(id)
    }
  }, [delay])
}

export const useUniqueID = id => useMemo(() => id?.length ? id : Math.random().toString(), [id])

export const useWindowSize = () => {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  })

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // Add event listener
    window.addEventListener('resize', handleResize)

    // Call handler right away so state gets updated with initial window size
    handleResize()

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize)
  }, []) // Empty array ensures that effect is only run on mount

  return windowSize
}

export const useBreakpoints = () => {
  const { width } = useWindowSize()

  return useMemo(() => ({
    isLoading: width === undefined,
    xx: width >= breakpoints.xx,
    xs: width >= breakpoints.xs,
    sm: width >= breakpoints.sm,
    md: width >= breakpoints.md,
    lg: width >= breakpoints.lg,
    xl: width >= breakpoints.xl,
    xxDown: width < breakpoints.xx,
    xsDown: width < breakpoints.xs,
    smDown: width < breakpoints.sm,
    mdDown: width < breakpoints.md,
    lgDown: width < breakpoints.lg,
    xlDown: width < breakpoints.xl,
    current:
      width >= breakpoints.xl
        ? 'xl'
        : width >= breakpoints.lg
        ? 'lg'
        : width >= breakpoints.md
        ? 'md'
        : width >= breakpoints.sm
        ? 'sm'
        : width >= breakpoints.xs
        ? 'xs'
        : 'xx',
    isTouch: 'ontouchstart' in document.documentElement,
    isSafariMobile: isSafari && width < breakpoints.md,
  }), [width, isSafari])
}

export const useDebouncedCallback = (callback, delay, options = {}) => {
  const callbackRef = useRef()
  callbackRef.current = callback
  return useCallback(debounce((...args) => callbackRef.current(...args), delay, options), [])
}

export const useSortFilterSearchPagination = ({
  items = [],
  defaultSortType = 'first_name',
  defaultIsDesc = true,
  defaultStatusFilter = 'all',
  perPage = 25,
  defaultPage = 1,
  isPaginationDisabled = false,
  numberOfFilters,
  isFilteredOut = () => {},
  setSearchableValue = () => {},
  customSort,
}) => {
  const [searchable, setSearchable] = useState([])
  const [sortType, setSortType] = useState(defaultSortType)
  const [isDesc, setIsDesc] = useState(defaultIsDesc)
  const [filteredUniqueIDs, setFilteredUniqueIDs] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortedItems, setSortedItems] = useState([])
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter)
  const [statusFilteredItems, setStatusFilteredItems] = useState([])
  const [page, setPage] = useState(defaultPage)
  const [refresh, setRefresh] = useState(false)
  const searchFilterOn = useMemo(() => !!searchValue.length, [searchValue])
  const itemsStr = useMemo(() => JSON.stringify(items), [items])

  const inRange = useCallback(index => isPaginationDisabled ? true : index >= (page - 1) * perPage && index < page * perPage, [
    isPaginationDisabled,
    page,
    perPage,
  ])

  const showItem = useCallback(id => searchFilterOn ? filteredUniqueIDs.includes(id) : true, [
    searchFilterOn,
    filteredUniqueIDs,
  ])
  // return isPaginationDisabled ? true : searchFilterOn ? filteredUniqueIDs.includes(id) : true

  useEffect(() => {
    if (!isEmpty(items)) {
      setSearchable(
        items.map(item => ({
          val: setSearchableValue(item)?.toLowerCase() || '',
          unique_id: item.unique_id,
        }))
      )
    }
  }, [items.length])

  useEffect(() => {
    let temp = cloneDeep(statusFilteredItems)
    if (customSort) {
      temp = customSort(temp, sortType, isDesc)
    } else {
      if (!isEmpty(temp)) {
        if (sortType.includes('date')) {
          temp = temp.map(o => ({ ...o, [sortType]: moment(o[sortType]) }))
        } else if (
          isNumber(+temp[0]?.[sortType]) &&
          !isNaN(+temp[0]?.[sortType])
        ) {
          temp = temp.map(o => ({ ...o, [sortType]: +o[sortType] }))
        }
        temp = orderBy(temp, sortType, isDesc ? 'desc' : 'asc')
      }
    }
    setSortedItems(temp || [])
    setStatusFilteredItems(temp || [])
  }, [refresh, sortType, isDesc])

  useEffect(() => {
    let temp = filter(searchable, item =>
      item.val.includes(searchValue?.toLowerCase())
    )
    setFilteredUniqueIDs(temp.map(item => item.unique_id))
  }, [searchValue])

  useEffect(() => {
    setStatusFilteredItems(
      filter(items, item => !isFilteredOut(item, statusFilter)) || []
    )
    setRefresh(!refresh)
  }, [itemsStr, statusFilter, numberOfFilters])

  const pageCount = useMemo(() => Math.ceil(statusFilteredItems.length / perPage), [statusFilteredItems.length, perPage])

  const showingRange = useMemo(() => `${(page - 1) * perPage + 1}–${Math.min(page * perPage, statusFilteredItems.length)}`, [page, perPage, statusFilteredItems.length])

  const totalItems = useMemo(() => statusFilteredItems.length, [statusFilteredItems.length])

  const showPagination = useMemo(() => isPaginationDisabled ? false : statusFilteredItems.length > perPage && !searchFilterOn, [isPaginationDisabled, statusFilteredItems.length, perPage, searchFilterOn])

  const noResults = useMemo(() => (searchFilterOn && filteredUniqueIDs.length === 0) || !!!items.length, [searchFilterOn, filteredUniqueIDs.length, !!!items.length])

  const returnData = useMemo(() => ({
      statusFilteredItems,
      filteredUniqueIDs,
      searchValue,
      sortedItems,
      sortType,
      isDesc,
      searchFilterOn,
      pageCount,
      showingRange,
      totalItems,
      page,
      perPage,
      showPagination,
      noResults,
      setPage,
      setIsDesc,
      setSortType,
      setSearchValue,
      setStatusFilter,
      inRange,
      showItem,
    }), [
    statusFilteredItems,
    filteredUniqueIDs,
    searchValue,
    sortedItems,
    sortType,
    isDesc,
    searchFilterOn,
    pageCount,
    showingRange,
    totalItems,
    page,
    perPage,
    showPagination,
    noResults,
    setPage,
    setIsDesc,
    setSortType,
    setSearchValue,
    setStatusFilter,
    inRange,
    showItem,
  ])

  return returnData
}

export const useImagePreloader = imageUrls => {
  const [images, setImages] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadImages = async () => {
      const promises = imageUrls.map(async (url) => {
        const image = new Image()
        const promise = new Promise((resolve, reject) => {
          image.onload = resolve
          image.onerror = reject
        })
        image.src = url
        return promise
      })

      await Promise.all(promises)
      setImages(promises.reduce((acc, curr, i) => ({ ...acc, [imageUrls[i]]: curr }), {}))
      setLoading(false)
    }

    loadImages()
  }, [imageUrls])

  return [images, loading]
}

export const useTabFocus = callback => {
  const [tabFocused, setTabFocused] = useState(true)

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTabFocused(false)
      } else {
        setTabFocused(true)
        callback?.()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [callback])

  return tabFocused
}

const AGREEMENTS = {
  user: {
    key: GET_ONBOARDING_AGREEMENTS,
    value: 'agreements',
    setAgreementValue: 'agreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    checkboxLabel: 'By checking this box and clicking “I Understand & Agree”, you acknowledge that you are applying for a deposit account with Evolve Bank & Trust, Member FDIC, and that you agree to the documents above. You are also authorizing us to share your personal information with Evolve Bank & Trust.',
  },
  corporate: {
    key: GET_CORPORATE_ONBOARDING_AGREEMENTS,
    value: 'corporateAgreements',
    setAgreementValue: 'corporateAgreements',
    setAgreementType: SET_CORPORATE_ONBOARDING_AGREEMENTS,
    checkboxLabel: 'By checking this box and clicking “I Understand & Agree”, you acknowledge that you are applying for a deposit account with Evolve Bank & Trust, Member FDIC, and that you agree to the documents above. You are also authorizing us to share your personal information with Evolve Bank & Trust.',
  },

  transfer: {
    key: GET_BANK_AUTHORIZATION,
    agreement_type: 'transfer_authorization',
    value: 'transfer_authorization',
    setAgreementValue: 'agreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    autoConsent: true,
  },
  recurring_transfer: {
    key: GET_BANK_AUTHORIZATION,
    agreement_type: 'recurring_transfer_authorization',
    value: 'recurring_transfer_authorization',
    setAgreementValue: 'agreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    autoConsent: true,
  },
  recurring_cancel: {
    key: GET_BANK_AUTHORIZATION,
    agreement_type: 'recurring_cancel_authorization',
    value: 'recurring_cancel_authorization',
    setAgreementValue: 'agreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    autoConsent: true,
  },

  auto_reward: {
    key: GET_BANK_AUTHORIZATION,
    agreement_type: 'auto_reward_authorization',
    value: 'auto_reward_authorization',
    setAgreementValue: 'agreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    autoConsent: true,
  },
  reward: {
    key: GET_BANK_AUTHORIZATION,
    agreement_type: 'reward_authorization',
    value: 'reward_authorization',
    setAgreementValue: 'agreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    autoConsent: true,
  },
  bulk_reward: {
    key: GET_BANK_AUTHORIZATION,
    agreement_type: 'bulk_reward_authorization',
    value: 'bulk_reward_authorization',
    setAgreementValue: 'agreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    autoConsent: true,
  },
  recurring_kyc: {
    key: GET_BANK_AUTHORIZATION,
    agreement_type: 'recurring_kyc_authorization',
    value: 'recurring_kyc_authorization',
    setAgreementValue: 'agreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    autoConsent: true,
  },
  donde_internal: {
    key: GET_INTERNAL_ONBOARDING_AGREEMENTS,
    value: 'internalAgreements',
    setAgreementType: SET_ONBOARDING_AGREEMENTS,
    setAgreementValue: 'agreements',
  },
  // donde_internal_corp: {
  //   key: GET_CORPORATE_ONBOARDING_AGREEMENTS,
  //   value: 'corporateAgreements',
  //   setAgreementType: SET_ONBOARDING_AGREEMENTS,
  //   setAgreementValue: 'agreements',
  // },
}

export const useAgreements = ({ type, onSuccess }) => {
  const dispatch = useDispatch()
  const agreementType = AGREEMENTS[type]
  const [consent, setConsent] = useState(false)
  const agreements = useSelector(({ global }) => global[agreementType.value]) || []
  const acceptance_id = useSelector(({ global }) => global[`acceptance_id_${agreementType.agreement_type}`])
  const { isLoading } = useTypeLoad(agreementType.key)
  const groupedAgreements = groupBy(agreements, 'agreement_group_name')

  const onNext = () => {
    if (consent || agreementType.autoConsent) {
      dispatch({
        type: agreementType.setAgreementType,
        [agreementType.setAgreementValue]: agreements.map(o => o.agreement_id),
        agreement_type: agreementType.agreement_type,
      })
    }
  }

  const setLoading = useTypeLoad(agreementType.setAgreementType, data => {
    if (!data.error) onSuccess?.(acceptance_id)
  })

  useEffect(() => {
    if (isEmpty(agreements)) {
      dispatch({
        type: agreementType.key,
        agreement_type: agreementType.agreement_type,
      })
    }
  }, [agreementType.key])

  return {
    consent,
    agreementsLoading: isLoading,
    agreements: groupedAgreements,
    ungroupedAgreements: agreements,
    authLanguage: agreements.length === 1 ? get(agreements, '0.description')  : '',
    onNext,
    setConsent,
    ...agreementType,
  }
}

export const useHeaderNavigation = () => {
  const navigate = useNavigate()
  const location = useLocation()

  return to => {
    if (HOST.includes(location.hostname)) {
      navigate(to)
    } else {
      window.location = `${BASE_URL}${to}`
    }
  }
}

export const useTypingAnimation = (disabled, phrases = [], delay = 50, pauseTime = 2000) => {
  const [index, setIndex] = useState(0)
  const [subIndex, setSubIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [placeholder, setPlaceholder] = useState('')
  
  const timeoutRef = useRef(null)

  useEffect(() => {
    if (index >= phrases.length || disabled) {
      return
    }

    if (subIndex === phrases[index].length + 1 && !isDeleting) {
      setTimeout(() => setIsDeleting(true), pauseTime)
      return
    }

    if (subIndex === 0 && isDeleting) {
      setIsDeleting(false)
      setIndex(prevIndex => prevIndex + 1)
      return
    }

    timeoutRef.current = setTimeout(() => {
      setPlaceholder(phrases[index].substring(0, subIndex))
      if (subIndex === 0 && isDeleting) return
      setSubIndex(prevSubIndex => isDeleting ? 0 : prevSubIndex + 1)
    }, delay)

    return () => clearTimeout(timeoutRef.current)

  }, [disabled, subIndex, index, isDeleting])

  return disabled ? 'Search' : placeholder
}

export const useControlledTypingAnimation = (phrase, delay = 50) => {
  const [subIndex, setSubIndex] = useState(0)
  const [placeholder, setPlaceholder] = useState('')
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('')
  const { mdDown } = useBreakpoints()

  const timeoutRef = useRef(null)

  useEffect(() => {
    setSubIndex(0)
  }, [phrase])

  useEffect(() => {
    if (subIndex > phrase.length) return

    timeoutRef.current = setTimeout(() => {
      setAnimatedPlaceholder(phrase.substring(0, subIndex))
      setPlaceholder(phrase)
      setSubIndex(prevSubIndex => prevSubIndex + 1)
    }, delay)

    return () => clearTimeout(timeoutRef.current)
  }, [subIndex, phrase])

  return {
    animatedPlaceholder: mdDown ? placeholder : animatedPlaceholder,
    placeholder,
  }
}